import { useEffect, useState } from 'react';
import type { CampaignLeadForm } from '../../../types/campaign';
import type { FormType } from '../../../types/franchiseLocationProfile';
import {
  addLeadForm,
  deleteLeadForm,
  patchLeadForm,
  renameLeadForm,
  setDefaultLeadForm,
} from '../../../lib/campaignLeadForms';
import { LeadFormEditor } from './LeadFormEditor';

// Multi Lead Form spec §2.1–2.4. Tabbed authoring UI inside the Campaign
// editor's Lead Forms section. Each tab is one CampaignLeadForm entry;
// the last tab is a "+" affordance that appends a new entry (named via
// browser prompt). Selecting a tab swaps which entry the LeadFormEditor
// renders; the active tab also surfaces Rename / Delete / Set as default
// affordances.
//
// Rules (enforced by lib/campaignLeadForms.ts pure helpers):
//   - Can't delete the last form (always at least one).
//   - Can't delete the form that is currently default (must reassign first).
//   - Name uniqueness within the Campaign (case-insensitive).
//
// State management:
//   - `value` + `defaultLeadFormId` are controlled by the parent form
//     state (CampaignForm.tsx).
//   - The tab strip owns local selection state (which tab is open) plus
//     a transient error string surfaced inline for the most recent op.

const FORM_TYPES: FormType[] = ['Generic', 'Qualifying Question'];

export type LeadFormTabStripProps = {
  value: CampaignLeadForm[];
  defaultLeadFormId: string;
  onChange: (forms: CampaignLeadForm[], defaultLeadFormId: string) => void;
};

export function LeadFormTabStrip({
  value,
  defaultLeadFormId,
  onChange,
}: LeadFormTabStripProps) {
  // `value` is guaranteed non-empty by the form-state initialisers; we
  // still defend the boundary so a transient state doesn't crash.
  const initialId = value.find((f) => f.id === defaultLeadFormId)?.id ?? value[0]?.id ?? null;
  const [activeId, setActiveId] = useState<string | null>(initialId);
  const [error, setError] = useState<string | null>(null);

  // Re-pin selection if the active form is deleted upstream (e.g. parent
  // state replaces the array). Falls back to default, then first entry.
  useEffect(() => {
    if (!activeId || !value.some((f) => f.id === activeId)) {
      setActiveId(value.find((f) => f.id === defaultLeadFormId)?.id ?? value[0]?.id ?? null);
    }
  }, [value, defaultLeadFormId, activeId]);

  const active = value.find((f) => f.id === activeId) ?? null;

  const runOp = (
    op: () => ReturnType<typeof addLeadForm>,
    onSuccessId?: (id: string) => void,
  ) => {
    const result = op();
    if (!result.ok) {
      setError(result.error);
      return;
    }
    setError(null);
    onChange(result.forms, result.defaultLeadFormId);
    if (onSuccessId) {
      const lastNew = result.forms[result.forms.length - 1];
      if (lastNew) onSuccessId(lastNew.id);
    }
  };

  const handleAdd = () => {
    const name = window.prompt('Name this lead form');
    if (!name || !name.trim()) return;
    runOp(
      () => addLeadForm(value, defaultLeadFormId, name),
      (id) => setActiveId(id),
    );
  };

  const handleRename = () => {
    if (!active) return;
    const name = window.prompt('Rename lead form', active.name);
    if (name === null) return;
    runOp(() => renameLeadForm(value, defaultLeadFormId, active.id, name));
  };

  const handleDelete = () => {
    if (!active) return;
    const ok = window.confirm(`Delete lead form "${active.name}"? Rows still using it will need reassign.`);
    if (!ok) return;
    runOp(() => deleteLeadForm(value, defaultLeadFormId, active.id));
  };

  const handleSetDefault = () => {
    if (!active) return;
    runOp(() => setDefaultLeadForm(value, defaultLeadFormId, active.id));
  };

  const handleFieldsChange = (next: CampaignLeadForm['fields']) => {
    if (!active) return;
    onChange(patchLeadForm(value, active.id, { fields: next }), defaultLeadFormId);
  };

  const handleFormTypeChange = (next: FormType) => {
    if (!active) return;
    onChange(patchLeadForm(value, active.id, { formType: next }), defaultLeadFormId);
  };

  return (
    <div className="space-y-4">
      <div
        className="flex flex-wrap items-center gap-2 border-b border-slate-200 pb-2"
        role="tablist"
        aria-label="Lead forms"
      >
        {value.map((lf) => {
          const selected = lf.id === activeId;
          const isDefault = lf.id === defaultLeadFormId;
          return (
            <button
              key={lf.id}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => {
                setActiveId(lf.id);
                setError(null);
              }}
              className={`px-3 py-1.5 rounded-md text-sm flex items-center gap-1.5 ${
                selected
                  ? 'bg-slate-900 text-white'
                  : 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50'
              }`}
            >
              <span>{lf.name}</span>
              {isDefault && (
                <span
                  className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${
                    selected ? 'bg-slate-700 text-white' : 'bg-slate-900 text-white'
                  }`}
                >
                  default
                </span>
              )}
            </button>
          );
        })}
        <button
          type="button"
          onClick={handleAdd}
          className="px-3 py-1.5 rounded-md text-sm bg-white text-slate-700 border border-dashed border-slate-300 hover:bg-slate-50"
          title="Add a new lead form"
        >
          + Add form
        </button>
      </div>

      {error && (
        <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700">
          {error}
        </div>
      )}

      {active && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-slate-700">
              Editing: <span className="font-medium text-slate-900">{active.name}</span>
            </span>
            {active.id === defaultLeadFormId ? (
              <span className="text-xs text-slate-500 italic">(default)</span>
            ) : (
              <button
                type="button"
                onClick={handleSetDefault}
                className="text-xs text-blue-600 hover:underline"
              >
                Set as default
              </button>
            )}
            <span className="flex-1" />
            <button
              type="button"
              onClick={handleRename}
              className="text-xs px-2 py-1 rounded-md border border-slate-300 bg-white hover:bg-slate-50"
            >
              Rename
            </button>
            <button
              type="button"
              onClick={handleDelete}
              className="text-xs px-2 py-1 rounded-md border border-red-300 bg-white text-red-700 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={value.length <= 1 || active.id === defaultLeadFormId}
              title={
                value.length <= 1
                  ? 'Campaign must have at least one lead form.'
                  : active.id === defaultLeadFormId
                    ? 'Set a different default before deleting this one.'
                    : undefined
              }
            >
              Delete
            </button>
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium text-slate-900">Form type</label>
            <select
              className="rounded-md border border-slate-300 px-3 py-2 text-sm"
              value={active.formType}
              onChange={(e) => handleFormTypeChange(e.target.value as FormType)}
            >
              {FORM_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-slate-900 mb-3">Form fields</h3>
            <LeadFormEditor value={active.fields} onChange={handleFieldsChange} />
          </div>
        </div>
      )}
    </div>
  );
}
